import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Form, Grid, message, Modal } from 'antd';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type LocalVariantWithPermissionsSpecsFragment } from '../../../api/fragments/LocalVariantWithPermissionsSpecs';
import {
    DeleteVehicleDocument,
    type DeleteVehicleMutation,
    type DeleteVehicleMutationVariables,
} from '../../../api/mutations/deleteVehicle';
import {
    UpdateVariantDocument,
    type UpdateVariantMutation,
    type UpdateVariantMutationVariables,
} from '../../../api/mutations/updateVariant';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import AddVariantDetails from '../AddVehiclePage/AddVariantPage/AddVariantDetails';
import { type FormValues } from '../AddVehiclePage/AddVariantPage/types';
import useVariantValidator from '../AddVehiclePage/AddVariantPage/useVariantValidator';
import useHandleVariantAssetsUpdate from './useHandleVariantAssetsUpdate';
import useVariantAssetValidation from './useVariantAssetValidation';
import useVariantImagesOrdering from './useVariantImagesOrdering';

export type VariantDetailsPageProps = { variant: LocalVariantWithPermissionsSpecsFragment };

const VariantDetailsPage = ({ variant }: VariantDetailsPageProps) => {
    const { t } = useTranslation(['variant']);
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const variantAssetValidation = useVariantAssetValidation();
    const handleVariantAssetsUpdate = useHandleVariantAssetsUpdate();
    const variantImagesOrdering = useVariantImagesOrdering();

    const validator = useVariantValidator();
    const screens = Grid.useBreakpoint();

    const variantId = variant.id;
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: variant.versioning,
        timeZone: variant.module.company.timeZone,
    });

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(
                [
                    'name',
                    'identifier',
                    'description',
                    'modelId',
                    'isActive',
                    'order',
                    'vehiclePrice',
                    'images',
                    'brochure',
                    'engineType',
                    'engine',
                    'features',
                    'powerOutput',
                    'topSpeed',
                    'bodyType',
                    'submodelId',
                    'seats',
                    'transmission',
                    'appleCarPlay',
                    'energyConsumption',
                    'engineDisplacement',
                    'bankCodeMappings',
                ],
                variant
            ),
            modelYear: variant.modelYear ? dayjs().year(variant.modelYear) : null,
        }),
        [variant]
    );

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { images, brochure, ...settings } = values;

            // submitting message
            message.loading({
                content: t('variant:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            /*
            Check the assets first, if any of the new assets is broken / indicated malicious, 
            then don't proceed to update variant.

            Because in variant, instead of updating the existing one, it'll create new record for new version of variant
            Hence, since it'll be have a new variantId, then user can't click the save button & will show 404 error when
            reloading the page.

            */
            await variantAssetValidation(values);

            // submit update
            const { data } = await apolloClient
                .mutate<UpdateVariantMutation, UpdateVariantMutationVariables>({
                    mutation: UpdateVariantDocument,
                    variables: {
                        id: variantId,
                        settings: {
                            ...settings,
                            modelYear: settings.modelYear ? dayjs(settings.modelYear).year() : undefined,
                        },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            const { updatedVariantId, updatedImages } = await handleVariantAssetsUpdate(
                data.localVariant.id,
                values,
                variant
            );

            await variantImagesOrdering(updatedVariantId, values, variant, updatedImages);

            // inform about success
            message.success({
                content: t('variant:messages.updateSuccessful'),
                key: 'primary',
            });

            navigate('/admin/vehicles/variants');
        },
        [
            t,
            variantAssetValidation,
            apolloClient,
            variantId,
            handleVariantAssetsUpdate,
            variant,
            variantImagesOrdering,
            navigate,
        ],
        {
            picture: 'images',
        }
    );

    const deleteVariant = useCallback(() => {
        Modal.confirm({
            title: t('variant:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('variant:deleteModal.content'),
            okText: t('variant:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('variant:deleteModal.cancelText'),
            async onOk() {
                try {
                    message.loading({
                        content: t('variant:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with API
                    await apolloClient
                        .mutate<DeleteVehicleMutation, DeleteVehicleMutationVariables>({
                            mutation: DeleteVehicleDocument,
                            variables: { id: variantId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('variant:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    navigate('/admin/vehicles/variants');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, t, variantId, navigate]);

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(variant.permissions, [permissionKind.updateVehicle]),
            hasPermissions(variant.permissions, [permissionKind.deleteVehicle]),
        ],
        [variant]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validator}>
            {({ handleSubmit, isValid, dirty }) => (
                <ConsolePageWithHeader
                    extra={
                        hasDeletePermission && (
                            <Button onClick={deleteVariant} danger>
                                {t('variant:actions.delete')}
                            </Button>
                        )
                    }
                    footer={[
                        hasUpdatePermission && (
                            <Button
                                key="variantUpdate"
                                disabled={!dirty || !isValid}
                                form="variantUpdate"
                                htmlType="submit"
                                type="primary"
                            >
                                {t('variant:actions.update')}
                            </Button>
                        ),
                    ]}
                    onBack={() => navigate('/admin/vehicles/variants')}
                    title={
                        screens.md
                            ? t('variant:title', {
                                  name: initialValues.name.defaultValue,
                                  id: initialValues.identifier,
                              })
                            : `${initialValues.name.defaultValue} ${initialValues.identifier}`
                    }
                >
                    <Form id="variantUpdate" layout="vertical" name="variantUpdate" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <AddVariantDetails
                            companyId={variant?.model?.module?.companyId}
                            disabled={!hasUpdatePermission}
                            initialValues={initialValues}
                            offset={offset}
                            updated={updated}
                        />
                    </Form>
                </ConsolePageWithHeader>
            )}
        </Formik>
    );
};

export default VariantDetailsPage;
